import React from 'react';

import { Col } from 'reactstrap';
import styled, { useTheme } from 'styled-components';
import { prop } from 'styled-tools';

import CtaButton from 'components/CtaButton/CtaButton';
import RichTextField from 'components/RichTextField/RichTextField';
import StackHeader from 'components/StackHeader';

import { CtaButtonI, HeaderType } from 'interfaces/content-stack/contentStack.types';
import { ContentAlignmentType } from 'interfaces/styles';

import { textColumnShape } from 'shapes/components/textBlockShape';

import { columnSpanMap } from 'utils/constants';
import { getModularBlockLink } from 'utils/modularBlockUtils';

const TextColumnContainer = styled.div<{ $textAlign: ContentAlignmentType }>`
  text-align: ${prop('$textAlign', 'left')};
`;

const ButtonContainer = styled.div<{ $justify?: string }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: ${(props) => props.$justify ?? 'flex-start'};
  width: 100%;
  gap: 2rem;
  margin-top: 0.8rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

TextColumn.propTypes = {
  data: textColumnShape.isRequired,
};

interface TextColumnProps {
  body: string;
  contentAlignment: ContentAlignmentType;
  contentSpan: string;
  header: string;
  headerType: HeaderType;
  buttonCta: CtaButtonI[];
}

function TextColumn({ data }: { data: TextColumnProps }) {
  const theme = useTheme();
  const { body, buttonCta, contentAlignment, contentSpan, header, headerType } = data;
  const colCount = (contentSpan && columnSpanMap[contentSpan]) || true;

  const justifyButton = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  return (
    <Col sm={12} md={colCount}>
      <TextColumnContainer $textAlign={contentAlignment}>
        {header && (
          <StackHeader defaultHeaderType='H3' headerType={headerType} noPadding>
            {header}
          </StackHeader>
        )}
        {body && <RichTextField richText={body} />}
        {buttonCta && (
          <ButtonContainer $justify={justifyButton[contentAlignment]}>
            {buttonCta.map((button, index) => {
              const textColor = button?.buttonType === 'link' ? theme.colors.black : theme.colors.white;

              return (
                <CtaButton
                  to={getModularBlockLink(button.buttonLink)}
                  key={index}
                  buttonType={button?.buttonType}
                  text={button.buttonText}
                  textColor={textColor}
                  dataLayerInfo={header}
                />
              );
            })}
          </ButtonContainer>
        )}
      </TextColumnContainer>
    </Col>
  );
}

export default React.memo(TextColumn);
